import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import { useSound } from '../context/SoundContext';

const CookiePreferencesModal = () => {
  const { t } = useLanguage();
  const { consent, hasConsent, updatePreferences, acceptAll, openPreferences } = useCookieConsent();
  const { playSound } = useSound();
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [preferences, setPreferences] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('dios10:openCookiePreferences', handler);
    return () => window.removeEventListener('dios10:openCookiePreferences', handler);
  }, []);

  useEffect(() => {
    if (consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
      setPreferences(consent.preferences);
    } else {
      setAnalytics(false);
      setMarketing(false);
      setPreferences(false);
    }
  }, [consent, open]);

  if (!open) return null;

  const handleSave = () => {
    playSound?.('click');
    updatePreferences({ analytics, marketing, preferences });
    setOpen(false);
  };

  const handleAcceptAll = () => {
    playSound?.('click');
    acceptAll();
    setOpen(false);
  };

  const handleClose = () => {
    playSound?.('click');
    setOpen(false);
  };

  return (
    <div
      className="cookie-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="cookie-modal football-card glass-football">
        <div className="cookie-modal-header">
          <h2 id="cookie-modal-title" className="cookie-modal-title">{t('gdpr.preferencesTitle')}</h2>
          <button
            type="button"
            className="cookie-modal-close modal-close-btn"
            onClick={handleClose}
            aria-label={t('common.cancel')}
          >
            ×
          </button>
        </div>
        <div className="cookie-modal-body">
          <p className="cookie-modal-intro">{t('gdpr.preferencesIntro')}</p>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <span className="cookie-category-name">{t('gdpr.necessary')}</span>
              <span className="cookie-category-badge cookie-category-required">{t('gdpr.alwaysOn')}</span>
            </div>
            <p className="cookie-category-desc">{t('gdpr.necessaryDesc')}</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <span className="cookie-category-name">{t('gdpr.analytics')}</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">{t('gdpr.analyticsDesc')}</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <span className="cookie-category-name">{t('gdpr.preferencesCat')}</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences}
                  onChange={(e) => setPreferences(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">{t('gdpr.preferencesDesc')}</p>
          </div>

          <div className="cookie-category">
            <div className="cookie-category-header">
              <span className="cookie-category-name">{t('gdpr.marketing')}</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">{t('gdpr.marketingDesc')}</p>
          </div>
        </div>
        <div className="cookie-modal-footer">
          <button type="button" className="cookie-modal-btn cookie-modal-btn-secondary" onClick={handleClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="cookie-modal-btn cookie-modal-btn-outline" onClick={handleSave}>
            {t('gdpr.savePreferences')}
          </button>
          <button type="button" className="cookie-modal-btn btn-football" onClick={handleAcceptAll}>
            {t('gdpr.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
